import React from 'react'
import {
  checkIfCitiesHaveSequences,
  duplicateSelectedSequencesForCities,
  getCitiesForSequenceDuplication,
} from '../gql'
import { useDispatch } from 'react-redux'
import { Box } from '@material-ui/core'
import { Grid, Row } from 'react-bootstrap'
import {
  hideReduxModal,
  resetReduxModal,
  showReduxModal,
} from '../../../../../../Reducers/UI/actions'
import { CITY_STATUS_ENUM_ACTIVE } from '../../../../../../shared/constants'
import Form, { SelectMultiple } from 'Components/Form'
import { PATHS } from '../../../../../../Config/Routing/paths'

export const useDuplicateSequenceForCities = ({
  originalCityId,
  countryId,
}) => {
  const dispatch = useDispatch()

  const handleDuplicateSequenceForCities = async (cityIds, sequenceIds) => {
    const response = await duplicateSelectedSequencesForCities({
      cityIds,
      sequenceIds,
      originalCityId,
    })
    const payload = await response.json()
    const success = payload.data.duplicateSequencesToCities
    const failedCities = payload.data.duplicateSequencesToCities.failedCities

    hideReduxModal()
    resetReduxModal()

    let message = {
      title: 'Failure',
      message: 'Failed to duplicate sequences',
    }
    if (success) {
      message = {
        title: 'Success',
        text: 'The sequences were successfully copied.',
        failedCities: `The following cities were skipped because they do not have a default onboarding email configured in the city settings:  ${failedCities
          .map(c => c.name)
          .join(', ')}`,
      }
    }
    setTimeout(
      () =>
        dispatch(
          showReduxModal({
            title: message.title,
            children: (
              <Box
                display="flex"
                flexWrap="wrap"
                justifyContent="center"
                mt={3}
                mb={3}
              >
                <Grid>
                  <Row>
                    <p>{message.text}</p>
                  </Row>
                  {failedCities.length > 0 && (
                    <Row>
                      <p>
                        <br />
                        {message.failedCities}
                      </p>
                    </Row>
                  )}
                </Grid>
              </Box>
            ),
            onConfirm: async () => {
              hideReduxModal()
              resetReduxModal()
            },
            hideCancel: true,
            aspect: 'nonScrollable',
          }),
        ),
      350,
    )
  }

  const onCheckSequencesForDuplicates = async (cityIds, sequenceIds) => {
    const response = await checkIfCitiesHaveSequences({
      cityIds,
      sequenceIds,
    })
    const payload = await response.json()
    const result = payload.data.checkIfCitiesHaveSequences.citiesWithSequences

    if (result.length) {
      hideReduxModal()
      resetReduxModal()

      setTimeout(
        () =>
          dispatch(
            showReduxModal({
              title: 'Warning! Duplications found.',
              children: (
                <Box
                  display="flex"
                  flexDirection="column"
                  flexWrap="wrap"
                  mt={3}
                  mb={3}
                >
                  {result.map(item => (
                    <div>
                      <a
                        href={`${PATHS.main.dashboard}${
                          PATHS.dashboard.cities
                        }/${item.cityId}${PATHS.cities.ambassadorsEmails}`}
                        target="_blank"
                        style={{
                          textDecoration: 'none',
                          fontStyle: 'italic',
                          fontWeight: 'bold',
                          fontSize: '18px',
                        }}
                      >
                        {item.cityName}
                      </a>{' '}
                      : <span>{item.sequences.join(', ')}.</span>
                      <br />
                    </div>
                  ))}
                </Box>
              ),
              confirmLabel: 'Proceed Anyway',
              onConfirm: async () => {
                try {
                  await handleDuplicateSequenceForCities(cityIds, sequenceIds)
                  hideReduxModal()
                  resetReduxModal()
                } catch (error) {
                  hideReduxModal()
                  resetReduxModal()
                  alert(`Error occurred: ${error.message}`)
                }
              },
              onCancel: () => {
                hideReduxModal()
                resetReduxModal()
              },
              aspect: 'nonScrollable',
            }),
          ),
        350,
      )
    } else {
      await handleDuplicateSequenceForCities(cityIds, sequenceIds)
    }
  }

  const onDuplicateSequenceForCities = selectedSeqs => async () => {
    const fetchCities = async () => {
      try {
        const res = await getCitiesForSequenceDuplication({ countryId })
        const { data } = await res.json()
        const cities = data.country.cities.edges

        return cities.reduce((acc, c) => {
          const city = c.node
          if (
            city.statusV2 === CITY_STATUS_ENUM_ACTIVE &&
            city.id !== originalCityId
          ) {
            acc.push(city)
          }
          return acc
        }, [])
      } catch (e) {
        alert(`Error occurred: ${e.message}`)
      }
    }

    const cities = await fetchCities()
    let selectedCityIds = []

    const cityOptions = cities.map(c => ({ id: c.id, value: c.name }))

    dispatch(
      showReduxModal({
        title: 'Copy Sequences to Other Cities',
        children: (
          <Box
            display="flex"
            flexWrap="wrap"
            justifyContent="center"
            mt={3}
            mb={3}
          >
            <p>
              Please select the cities to which you want to copy the selected
              sequences from the list below <br />
              <span style={{ fontStyle: 'italic', fontWeight: 'bold' }}>
                Only active cities are displayed in the dropdown.
              </span>
            </p>
            <Form
              onUpdate={ev => {
                const values = ev.value
                if (values.some(v => v.id === 'all')) {
                  selectedCityIds = cities.map(c => c.id)
                } else {
                  selectedCityIds = values.map(v => v.id)
                }
              }}
            >
              <SelectMultiple
                title="Select cities"
                name="cities"
                options={cityOptions}
                searchable={true}
              />
            </Form>
          </Box>
        ),
        onConfirm: async () => {
          try {
            await onCheckSequencesForDuplicates(selectedCityIds, selectedSeqs)
            hideReduxModal()
            resetReduxModal()
          } catch (error) {
            hideReduxModal()
            resetReduxModal()
            alert(`Error occurred: ${error.message}`)
          }
        },
        onCancel: () => {
          hideReduxModal()
          resetReduxModal()
        },
        aspect: 'nonScrollable',
      }),
    )
  }

  return { onDuplicateSequenceForCities }
}
